import { useCallback, useContext, useEffect, useMemo } from 'react'
import { Form } from 'antd'
import { assign, isEmpty, isNil } from 'lodash-es'
import type { InnerNodePanelProps } from '@/features/nodes/base'

import { JsonForm, type JsonFormConfig } from '@/features/nodes/components'

import { Button } from '@/components'
import { useDynamicValues } from '../hooks/useDynamicValues'
import { DynamicForm } from '../base/components/DynamicForm'
import { DebugResultPanelContext } from '../base/DebugResultPanel'
import useStartFileNodeVariableKeyList from '../hooks/useStartFileNodeVariableKeyList'
import { FLOW_DRAFT_LOCK_STATUS, useFlowDraftStore } from '@/store'
import { useNodeBranchRemoveConfirm } from '../hooks/useNodeBranchRemoveConfirm'
import type { IntentItem } from './IntentForm'
import { BranchTypes, IntentForm, LLMModelSelect } from './IntentForm'
import type { IntentNodeData } from './IntentNode'

export function IntentPanelNode(props: InnerNodePanelProps<IntentNodeData>) {
  const {
    data,
    activatedNodeId,
    nodeElement,
    variables,
    startNodeFormItemType = [],
    onSaveChange,
  } = props

  const { conditions } = data
  const {
    registerTriggerNode,
    run,
    loading: btnLoading,
    variables: debugVariables,
    setVariables,
  } = useContext(DebugResultPanelContext)
  const [form] = Form.useForm()

  // const formRef = useRef<FormInstance>()
  const addBranch = useFlowDraftStore(s => s.addBranch)
  const removeBranch = useFlowDraftStore(s => s.removeBranch)
  const lockStatus = useFlowDraftStore(s => s.lockStatus)

  const { beforeRemove, cancel } = useNodeBranchRemoveConfirm(
    props.activatedNodeId!,
  )

  useEffect(() => {
    if (form) {
      const fieldValue = form.getFieldValue('conditions')
      // 当删除或增减的时候立即更新表单值
      if (fieldValue?.length !== props.data.conditions.length) {
        form.setFieldValue('conditions', props.data.conditions)
      }
    }
  }, [props.data.conditions, form, props])

  const onAdd = useCallback(async () => {
    const insertIndex = conditions.findIndex(
      item => item.type === BranchTypes.ELSE,
    )
    if (insertIndex > -1) {
      const res = await addBranch({
        id: props.activatedNodeId!,
        conditionIndex: insertIndex,
        condition: {
          type: BranchTypes.ELSEIF,
          statement: '',
        },
      })
      if (res) {
        const { nodes } = res
        const currentNode = nodes.find(
          (item: { id: string }) => item.id === props.activatedNodeId,
        )
        onSaveChange?.({ conditions: currentNode?.data?.conditions })
      }
    }
  }, [conditions, props.activatedNodeId])

  const onDelete = useCallback(
    async (id: string) => {
      const res = await removeBranch({
        id: props.activatedNodeId!,
        conditionId: id,
      })
      if (res) {
        const { nodes } = res
        const currentNode = nodes.find(
          (item: { id: string }) => item.id === props.activatedNodeId,
        )
        onSaveChange?.({ conditions: currentNode?.data?.conditions })
      }
    },
    [props.activatedNodeId, conditions],
  )

  const { fileVariableKeyList } = useStartFileNodeVariableKeyList(
    startNodeFormItemType,
    variables,
  )

  const val = useDynamicValues({
    nodeId: activatedNodeId!,
    data,
    values: form?.getFieldsValue(),
    variables: variables?.map(item => item.label),
    ruleCallback: (values: InnerNodePanelProps<any>['data']) => {
      const { conditions = [], inputs = {} } = values
      const { background, question } = inputs
      return [
        {
          content: background || '',
        },
        {
          content: question || '',
        },
        ...conditions.map((item: { statement: any }) => {
          return {
            content: item.statement || '',
          }
        }),
      ]
    },
  })

  const { usedKeyListByNodeId } = val

  const checkByBeforeInvoke = async () => {
    const val: any = {}
    usedKeyListByNodeId.forEach(item => {
      if (isNil(debugVariables[item])) {
        val[item] = ''
      }
    })

    let time = 0
    if (!isEmpty(val)) {
      await setVariables(val, true)
      time = 600
    }
    return new Promise(resolve => {
      setTimeout(() => {
        resolve('')
      }, time)
    })
  }

  const runBySingleStep = async () => {
    await checkByBeforeInvoke()
    await form?.validateFields()
    run(data)
  }

  const onDynamicValuesChange = (key: string, value: any) => {
    const usedVal: any = {}
    usedKeyListByNodeId?.forEach(item => {
      usedVal[item] = debugVariables[item] || ''
    })

    const values = assign({}, usedVal, { [key]: value })
    setVariables(values, false)
  }

  const list = useMemo<JsonFormConfig[]>(() => {
    return [
      {
        required: true,
        name: ['inputs'],
        disabled: lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK,
        label: '分类模型',
        render: () => <LLMModelSelect nodeElement={nodeElement} />,
      },
      {
        label: '用户问题',
        disabled: lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK,
        name: ['inputs', 'question'],
        required: true,
        type: 'TextEditor',
        rules: [{ required: true, message: '用户问题不能为空' }],
        componentProps: {
          placeholder:
            '输入需要识别意图的内容或者问题，如：“BetterYeah AI 的意图识别是怎么做的？”',
          variables,
          variableTipsContainer: nodeElement,
        },
      },
      {
        label: '背景知识',
        disabled: lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK,
        name: ['inputs', 'background'],
        type: 'TextEditor',
        tooltip:
          '当分类不准时，输入分类的规则说明和背景知识，帮助AI知道如何更准确地分类',
        componentProps: {
          placeholder: '输入背景知识',
          variables,
          variableTipsContainer: nodeElement,
        },
      },
      {
        name: 'conditions',
        label: '意图分类',
        required: true,
        // tooltip:
        //   '当分类不准时，输入分类的规则说明和背景知识，帮助AI知道如何更准确地分类',
        rules: [
          {
            required: true,
            validator: (_, value: IntentItem[]) => {
              const hasInvalidStatement = value
                .filter(v => v.type !== BranchTypes.ELSE)
                .some(item => item.statement === '')
              if (hasInvalidStatement) {
                return Promise.reject(new Error('请检查条件分支，不能为空'))
              }
              return Promise.resolve()
            },
          },
        ],
        render: () => {
          return (
            <IntentForm
              variables={props.variables}
              variableTipsContainer={props.nodeElement}
              onAdd={onAdd}
              onDelete={onDelete}
              onBeforeRemove={beforeRemove}
              onCancel={cancel}
              onChange={values => {
                onSaveChange?.({ conditions: values })
              }}
              disabled={lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK}
            />
          )
        },
      },
    ]
  }, [props.nodeElement, props.variables, onAdd, onDelete, lockStatus, props])

  useEffect(() => {
    registerTriggerNode(
      () => (
        <Button
          loading={btnLoading}
          type='primary'
          className='text-12px bg-op-60 !h-32px'
        >
          运行
        </Button>
      ),
      () => runBySingleStep(),
    )
  }, [data, btnLoading, debugVariables])

  return (
    <div>
      <div className='p-16px' onWheel={e => e.stopPropagation()}>
        <JsonForm list={list} form={form}></JsonForm>
        <div className='mt-16px'>
          <DynamicForm
            usedKeyList={usedKeyListByNodeId}
            dynamicValues={debugVariables}
            onValueChange={onDynamicValuesChange}
            fileVariableKeyList={fileVariableKeyList}
          />
        </div>
      </div>
    </div>
  )
}
